// packages/shared-components/src/components/ScaleSlider/ScaleSlider.tsx

import { useState, useRef, useEffect } from "react";
import { ScaleSliderProps } from "./types";
import styles from "./ScaleSlider.module.css";

export function ScaleSlider({
  value: controlledValue,
  defaultValue = 0,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  onChangeComplete,
  orientation = "vertical",
  showValue = true,
  showArrow = true,
  unit = "",
  precision = 2,
  scaleCount = 10,
  disabled = false,
  className = "",
  size = 200,
  trackWidth,
  showScaleValues = false,
  scaleWidth,
  showThumb = true,
}: ScaleSliderProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [hasError, setHasError] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isDraggingRef = useRef(false);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  // 竖向滑块：小值在顶部(bottom:100%), 大值在底部(bottom:0%)
  // 横向滑块：小值在左边(left:0%), 大值在右边(left:100%)
  const percentage =
    orientation === "vertical"
      ? ((max - value) / (max - min)) * 100 // 竖向反转
      : ((value - min) / (max - min)) * 100; // 横向保持

  const formatValue = (val: number) => {
    return val.toFixed(precision) + unit;
  };

  const parseInputValue = (
    input: string
  ): { value: number; isValid: boolean } => {
    // 移除单位后缀
    const numericStr = input.replace(unit, "").trim();
    const parsed = parseFloat(numericStr);

    if (isNaN(parsed)) {
      return { value: parsed, isValid: false };
    }

    // 应用步进值
    const steppedValue = Math.round(parsed / step) * step;

    // 检查是否在有效范围内
    const isValid = steppedValue >= min && steppedValue <= max;

    return { value: steppedValue, isValid };
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setIsEditing(true);
      setInputValue(value.toFixed(precision));
      setHasError(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setHasError(false);
  };

  const handleInputBlur = () => {
    const { value: parsedValue, isValid } = parseInputValue(inputValue);

    if (isValid) {
      // 值有效，保存
      if (!isControlled) {
        setInternalValue(parsedValue);
      }
      onChange?.(parsedValue);
      onChangeComplete?.(parsedValue);
      setIsEditing(false);
      setInputValue("");
      setHasError(false);
    } else {
      // 值无效或超出范围，显示错误状态
      setHasError(true);
      // 自动恢复原值并退出编辑
      setTimeout(() => {
        setIsEditing(false);
        setInputValue("");
        setHasError(false);
      }, 1000);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setInputValue("");
    }
  };

  // 计算输入框宽度（基于内容长度）
  const getInputWidth = () => {
    // 根据字符数估算宽度，每个字符约8px，加上 padding
    const charCount = Math.max(inputValue.length, formatValue(value).length);
    const estimatedWidth = charCount * 8.5 + 24; // 24px 为 padding
    return Math.max(60, Math.min(150, estimatedWidth)); // 最小60px，最大150px
  };

  // 进入编辑模式时自动聚焦并选中文本
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const updateValue = (clientX: number, clientY: number) => {
    if (!trackRef.current || disabled) return;

    const rect = trackRef.current.getBoundingClientRect();
    let percent = 0;

    if (orientation === "vertical") {
      // 竖向：鼠标在顶部对应小值(min)，在底部对应大值(max)
      percent = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    } else {
      percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    }

    const rawValue = min + percent * (max - min);
    const steppedValue = Math.round(rawValue / step) * step;
    const clampedValue = Math.max(min, Math.min(max, steppedValue));

    if (!isControlled) {
      setInternalValue(clampedValue);
    }
    onChange?.(clampedValue);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;
    setIsDragging(true);
    updateValue(e.clientX, e.clientY);
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingRef.current) {
        e.preventDefault();
        updateValue(e.clientX, e.clientY);
      }
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        setIsDragging(false);
        document.body.style.userSelect = "";
        // 触发拖动结束回调
        onChangeComplete?.(value);
      }
    };

    const handleMouseLeave = () => {
      if (isDraggingRef.current) handleMouseUp();
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.body.style.userSelect = "";
    };
  }, [
    min,
    max,
    step,
    disabled,
    onChange,
    onChangeComplete,
    isControlled,
    orientation,
    value,
  ]);

  // 生成刻度
  const scales = Array.from({ length: scaleCount + 1 }, (_, i) => {
    // position: i=0 在顶部(bottom:100%), i=scaleCount 在底部(bottom:0%)
    const position = ((scaleCount - i) / scaleCount) * 100;
    // value: i=0 对应 min(顶部显示小值), i=scaleCount 对应 max(底部显示大值)
    const value = min + (i / scaleCount) * (max - min);
    // 从顶部开始，每5个刻度为一个主刻度
    const isMain = i % 5 === 0;
    return {
      position,
      isMain,
      value: value.toFixed(2), // 保留两位小数
    };
  });

  const containerStyle: React.CSSProperties =
    orientation === "vertical"
      ? {
          height: `${size}px`,
          gridTemplateColumns: scaleWidth
            ? `${scaleWidth}px ${trackWidth || 8}px auto`
            : `auto ${trackWidth || 8}px auto`,
        }
      : {
          width: `${size}px`,
          gridTemplateRows: scaleWidth
            ? `${scaleWidth}px ${trackWidth || 8}px auto`
            : `auto ${trackWidth || 8}px auto`,
        };

  const containerClasses = `${styles.container} ${styles[orientation]} ${
    disabled ? styles.disabled : ""
  } ${className}`;

  const fillClasses = `${styles.fill} ${isDragging ? styles.fillNoDrag : ""}`;

  // ✅ 计算值显示的位置（跟随滑块）
  const valuePosition =
    orientation === "vertical"
      ? { bottom: `${percentage}%` }
      : { left: `${percentage}%` };

  return (
    <div ref={containerRef} className={containerClasses} style={containerStyle}>
      {/* ✅ 刻度区域（独立，不可交互） */}
      <div className={styles.scalesContainer}>
        {scales.map((scale, index) => (
          <div
            key={index}
            className={`${styles.scale} ${
              scale.isMain ? styles.scaleMain : styles.scaleMinor
            }`}
            style={
              orientation === "vertical"
                ? { bottom: `${scale.position}%` }
                : { left: `${scale.position}%` }
            }
          >
            {showScaleValues && scale.isMain && (
              <span className={styles.scaleValue}>{scale.value}</span>
            )}
          </div>
        ))}
      </div>

      {/* ✅ 滑动轨道区域（唯一可交互区域） */}
      <div
        ref={trackRef}
        className={styles.trackContainer}
        onMouseDown={handleMouseDown}
      >
        <div className={styles.track}>
          {/* 填充条 */}
          <div
            className={fillClasses}
            style={
              orientation === "vertical"
                ? { height: `${percentage}%` }
                : { width: `${percentage}%` }
            }
          />

          {/* 滑块 */}
          {showThumb && (
            <div
              className={styles.thumb}
              style={
                orientation === "vertical"
                  ? { bottom: `${percentage}%` }
                  : { left: `${percentage}%` }
              }
            />
          )}
        </div>
      </div>

      {/* ✅ 值显示区域（独立，不可交互） */}
      {showValue && (
        <div className={styles.valueContainer} style={valuePosition}>
          <div className={styles.valueContent}>
            {showArrow && (
              <svg className={styles.arrow} viewBox="0 0 12 12" fill="none">
                {orientation === "vertical" ? (
                  // 纵向：向左箭头 ←
                  <path
                    d="M8 10L4 6L8 2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ) : (
                  // 横向：向上箭头 ↑
                  <path
                    d="M10 8L6 4L2 8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
              </svg>
            )}
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                className={`${styles.valueInput} ${hasError ? styles.valueInputError : ""}`}
                style={{ width: `${getInputWidth()}px` }}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div className={styles.valueDisplay} onClick={handleInputClick}>
                {formatValue(value)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
